import { JUICE } from "./config.js";
import { createAmbientAudio, type AmbientAudio, type AmbientAudioContext } from "./ambientAudio.js";

export type { AmbientAudioContext };

export interface AudioJuice {
  unlock(): void;
  suspend(): void;
  resume(): void;
  playKick(speed: number): void;
  playPass(speed: number): void;
  playDash(): void;
  playTackle(): void;
  /** Levantar el balon: malabar corto o sombrero. */
  playLift(scoop: boolean): void;
  playNutmeg(): void;
  /** Golpeo de primera. `quality` 0-1: el contacto limpio suena distinto. */
  playFirstTime(quality: number): void;
  playCut(): void;
  playWall(): void;
  playPost(speed: number): void;
  playSave(): void;
  playGoal(): void;
  /** Ambiente continuo de estadio. */
  ambient: AmbientAudio;
  dispose(): void;
}

/**
 * Sonidos sintetizados con Web Audio. Cero assets externos; el contexto se
 * desbloquea con la primera interaccion del jugador.
 */
export function createAudioJuice(): AudioJuice {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;

  const ensure = (): AudioContext | null => {
    if (typeof AudioContext === "undefined") return null;
    if (!ctx) {
      ctx = new AudioContext();
      master = ctx.createGain();
      master.gain.value = JUICE.AUDIO.MASTER;
      master.connect(ctx.destination);
    }
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  };

  const getMaster = (): GainNode | null => {
    ensure();
    return master;
  };

  const ambient = createAmbientAudio(ensure, getMaster);

  const tone = (
    frequency: number,
    duration: number,
    type: OscillatorType = "sine",
    gain = 0.25,
    detune = 0,
  ): void => {
    const context = ensure();
    if (!context || !master) return;

    const osc = context.createOscillator();
    const env = context.createGain();
    osc.type = type;
    osc.frequency.value = frequency;
    osc.detune.value = detune;

    const now = context.currentTime;
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(gain, now + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    osc.connect(env);
    env.connect(master);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  };

  const noise = (duration: number, gain = 0.12): void => {
    const context = ensure();
    if (!context || !master) return;

    const bufferSize = Math.floor(context.sampleRate * duration);
    const buffer = context.createBuffer(1, bufferSize, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

    const source = context.createBufferSource();
    source.buffer = buffer;

    const filter = context.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 900;
    filter.Q.value = 0.6;

    const env = context.createGain();
    const now = context.currentTime;
    env.gain.setValueAtTime(gain, now);
    env.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter);
    filter.connect(env);
    env.connect(master);
    source.start(now);
    source.stop(now + duration + 0.02);
  };

  return {
    unlock(): void {
      ensure();
      ambient.start();
    },

    suspend(): void {
      void ctx?.suspend();
    },

    resume(): void {
      if (ctx?.state === "suspended") void ctx.resume();
    },

    playKick(speed: number): void {
      const power = Math.min(1, speed / 1650);
      tone(95 + power * 95, 0.08 + power * 0.04, "triangle", 0.2 + power * 0.18);
      tone(180 + power * 120, 0.05, "sine", 0.08 + power * 0.1, 40);
      noise(0.04 + power * 0.04, 0.08 + power * 0.14);
    },

    playPass(speed: number): void {
      const power = Math.min(1, speed / 950);
      tone(180 + power * 40, 0.06, "sine", 0.14);
      noise(0.03, 0.04);
    },

    playDash(): void {
      tone(160, 0.05, "sawtooth", 0.12);
      noise(0.06, 0.09);
    },

    playLift(scoop: boolean): void {
      // Sombrero = barrido ascendente y aireado; malabar = toque seco y corto.
      if (scoop) {
        tone(420, 0.16, "sine", 0.12);
        tone(680, 0.2, "sine", 0.09, 25);
        noise(0.05, 0.03);
      } else {
        tone(520, 0.07, "triangle", 0.1);
        noise(0.02, 0.02);
      }
    },

    /**
     * Golpeo de primera. El sonido ES la recompensa: un contacto perfecto
     * suena a chasquido limpio y afinado, y uno malo a golpe sordo y sucio.
     * Sin esta diferencia el jugador no puede aprender el timing de oido, que
     * es como se aprende de verdad este tipo de gesto.
     */
    playFirstTime(quality: number): void {
      const q = Math.max(0, Math.min(1, quality));
      // Chasquido: sube de tono y de cuerpo con la calidad.
      tone(180 + q * 140, 0.05 + q * 0.03, "square", 0.12 + q * 0.14);
      if (q > 0.5) {
        // Armonico brillante: solo aparece cuando el contacto es bueno, asi que
        // el "ping" agudo se convierte en la señal de que lo has clavado.
        tone(640 + q * 520, 0.1 + q * 0.08, "sine", 0.05 + (q - 0.5) * 0.18, 60);
      }
      // El mal contacto mete mas ruido: suena a pierna, no a balon.
      noise(0.05 + (1 - q) * 0.06, 0.06 + (1 - q) * 0.1);
    },

    playNutmeg(): void {
      tone(380, 0.05, "triangle", 0.11);
      tone(720, 0.09, "sine", 0.08, 40);
      noise(0.04, 0.05);
    },

    playCut(): void {
      tone(240, 0.045, "sawtooth", 0.1);
      noise(0.035, 0.06);
    },

    playTackle(): void {
      tone(110, 0.07, "square", 0.18);
      noise(0.08, 0.11);
    },

    playWall(): void {
      tone(90, 0.05, "square", 0.1);
      noise(0.04, 0.08);
    },

    playPost(speed: number): void {
      const power = Math.min(1, speed / 1200);
      tone(180 + power * 120, 0.07 + power * 0.03, "triangle", 0.14 + power * 0.12);
      tone(320 + power * 80, 0.04, "sine", 0.08 + power * 0.06, 30);
      noise(0.05 + power * 0.03, 0.1 + power * 0.08);
    },

    playSave(): void {
      tone(220, 0.06, "square", 0.16);
      noise(0.05, 0.07);
    },

    playGoal(): void {
      tone(330, 0.12, "triangle", 0.22);
      tone(440, 0.12, "triangle", 0.2, 80);
      tone(550, 0.18, "triangle", 0.18, 160);
      noise(0.25, 0.14);
      ambient.onGoal();
    },

    ambient,

    dispose(): void {
      ambient.dispose();
      void ctx?.suspend();
      void ctx?.close();
      ctx = null;
      master = null;
    },
  };
}
